import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { sendFissalChat, getFrigateSnapshotUrl } from '../services/api';
import { triggerHaptic } from '../services/purrSynthesizer';
import { ContextBreadcrumb } from '../components/ContextBreadcrumb';
import { Image, Video, Lightbulb, FileText, CheckCircle, RefreshCw, Send, ShieldCheck, ExternalLink, Heart } from 'lucide-react';

export const FamilyLivingRoomView: React.FC = () => {
  const { status, refreshStatus } = useApp();
  const [cacheBuster, setCacheBuster] = useState(Date.now());
  const [noteMessage, setNoteMessage] = useState('');
  const [noteSender, setNoteSender] = useState('Mom');
  const [noteStatus, setNoteStatus] = useState<string | null>(null);
  const [sendingNote, setSendingNote] = useState(false);
  const [healingGlitch, setHealingGlitch] = useState<string | null>(null);

  // Tuya Smart Lights State
  const [lights, setLights] = useState([
    { id: '1', name: 'Living Room Ceiling', isOn: true, color: '#fbbf24' },
    { id: '2', name: 'Cozy Lamp', isOn: true, color: '#f59e0b' },
    { id: '3', name: 'Kitchen Bar', isOn: false, color: '#ffffff' },
    { id: '4', name: 'Porch Light', isOn: true, color: '#38bdf8' },
  ]);

  const toggleLight = (id: string) => {
    triggerHaptic(25);
    setLights((prev) =>
      prev.map((l) => (l.id === id ? { ...l, isOn: !l.isOn } : l))
    );
  };

  const setScene = (sceneName: string) => {
    triggerHaptic([30, 40, 30]);
    if (sceneName === 'all_off') {
      setLights((prev) => prev.map((l) => ({ ...l, isOn: false })));
    } else if (sceneName === 'cozy') {
      setLights((prev) =>
        prev.map((l) => ({ ...l, isOn: l.id === '2' || l.id === '1' }))
      );
    } else if (sceneName === 'all_on') {
      setLights((prev) => prev.map((l) => ({ ...l, isOn: true })));
    }
  };

  const handleFixGlitch = async (service: 'photos' | 'cameras') => {
    setHealingGlitch(service);
    triggerHaptic([30, 40, 30]);
    try {
      const res = await sendFissalChat(`Please fix_household_glitch for ${service}`, [], 'family');
      alert(res.answer);
      await refreshStatus();
    } catch (e: any) {
      alert(`Service refresh failed: ${e.message}`);
    } finally {
      setHealingGlitch(null);
    }
  };

  const handleSendNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteMessage.trim() || sendingNote) return;

    setSendingNote(true);
    triggerHaptic(30);

    try {
      const res = await sendFissalChat(
        `Please leave_echo_a_note with message: "${noteMessage}" from ${noteSender}`,
        [],
        'family'
      );
      setNoteStatus(`Saved! Echo will see your note on his desk.`);
      setNoteMessage('');
      setTimeout(() => setNoteStatus(null), 5000);
    } catch (e: any) {
      setNoteStatus(`Could not save note: ${e.message}`);
    } finally {
      setSendingNote(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-3.5 py-3 space-y-4">
      <ContextBreadcrumb realmName="Family Realm" deckTitle="Living Room" realmEmoji="🛋️" />
      {/* The Relay Hero Card */}
      <div className="rounded-2xl p-4 bg-gradient-to-br from-emerald-950/40 via-slate-900/60 to-slate-950 border border-emerald-500/30 shadow-xl">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-lg">
            🏰
          </div>
          <div>
            <h2 className="font-cinzel text-sm font-bold text-emerald-300 tracking-wider">
              CASTLE ECHO LIVING ROOM
            </h2>
            <p className="text-[11px] text-slate-300">
              Welcome home. All household memories and security are running smoothly.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/10 text-xs text-slate-300">
          <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>Pi-hole Ad-Shield is blocking malicious trackers for all home devices.</span>
        </div>
      </div>

      {/* Family Photos (Immich) */}
      <div className="glass-panel rounded-2xl p-3.5 border border-white/10 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Image className="w-4 h-4 text-emerald-400" />
            <h3 className="font-semibold text-xs text-white">Family Photo Vault (Immich)</h3>
          </div>
          <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/15 px-2 py-0.5 rounded-full border border-emerald-500/30">
            Backed Up & Safe
          </span>
        </div>

        <p className="text-xs text-slate-300 leading-relaxed">
          Your full-quality original photos and videos are stored safely right here at home, not on big tech servers.
        </p>

        <div className="flex items-center gap-2 pt-1">
          <a
            href="https://photos.ech-o.net"
            target="_blank"
            rel="noreferrer"
            className="flex-1 py-2 px-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold text-xs flex items-center justify-center gap-1.5 transition-all"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open Photo Vault
          </a>

          <button
            onClick={() => handleFixGlitch('photos')}
            disabled={healingGlitch === 'photos'}
            className="py-2 px-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-slate-300 hover:text-white flex items-center gap-1 transition-all"
            title="Gently refresh photo server if stuck"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${healingGlitch === 'photos' ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Security Cameras (Frigate) */}
      <div className="glass-panel rounded-2xl p-3.5 border border-white/10 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Video className="w-4 h-4 text-sky-400" />
            <h3 className="font-semibold text-xs text-white">Security Cameras (Frigate)</h3>
          </div>
          <span className="text-[10px] font-mono text-sky-400 bg-sky-500/15 px-2 py-0.5 rounded-full border border-sky-500/30">
            Recording Active
          </span>
        </div>

        <p className="text-xs text-slate-300 leading-relaxed">
          Front Porch and Driveway cameras are watching the house with smart AI detection.
        </p>

        {/* Live Snapshots */}
        <div className="grid grid-cols-2 gap-2 my-1">
          <div className="relative rounded-xl overflow-hidden aspect-video bg-black/80 border border-white/10">
            <img
              src={getFrigateSnapshotUrl('e-roto_1', cacheBuster)}
              alt="Main Yard"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 inset-x-0 bg-black/70 px-2 py-0.5 text-[9px] font-mono text-white truncate">
              Main Yard
            </div>
          </div>
          <div className="relative rounded-xl overflow-hidden aspect-video bg-black/80 border border-white/10">
            <img
              src={getFrigateSnapshotUrl('ipcam_100', cacheBuster)}
              alt="Dogshop Porch"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 inset-x-0 bg-black/70 px-2 py-0.5 text-[9px] font-mono text-white truncate">
              Dogshop Porch
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <a
            href="https://frigate.ech-o.net"
            target="_blank"
            rel="noreferrer"
            className="flex-1 py-2 px-3 rounded-xl bg-sky-500 hover:bg-sky-600 text-slate-950 font-bold text-xs flex items-center justify-center gap-1.5 transition-all"
          >
            <ExternalLink className="w-3.5 h-3.5" /> View Live Cameras
          </a>

          <button
            onClick={() => handleFixGlitch('cameras')}
            disabled={healingGlitch === 'cameras'}
            className="py-2 px-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-slate-300 hover:text-white flex items-center gap-1 transition-all"
            title="Gently refresh cameras if feed pauses"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${healingGlitch === 'cameras' ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tuya Smart Lights */}
      <div className="glass-panel rounded-2xl p-3.5 border border-white/10 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-fissal-gold" />
            <h3 className="font-semibold text-xs text-white">Tuya Smart Lights</h3>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setScene('cozy')}
              className="px-2 py-1 rounded-lg bg-amber-500/20 text-amber-300 text-[10px] font-semibold border border-amber-500/30"
            >
              Cozy
            </button>
            <button
              onClick={() => setScene('all_off')}
              className="px-2 py-1 rounded-lg bg-white/10 text-slate-300 text-[10px] font-semibold hover:bg-white/20"
            >
              All Off
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {lights.map((l) => (
            <button
              key={l.id}
              onClick={() => toggleLight(l.id)}
              className={`p-3 rounded-xl border text-left transition-all ${
                l.isOn
                  ? 'bg-amber-500/20 border-fissal-gold/50 text-white shadow-quantum-glow'
                  : 'bg-black/30 border-white/10 text-slate-400'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <Lightbulb className={`w-4 h-4 ${l.isOn ? 'text-fissal-gold' : 'text-slate-500'}`} />
                <span className="text-[10px] font-mono font-bold">
                  {l.isOn ? 'ON' : 'OFF'}
                </span>
              </div>
              <div className="text-xs font-semibold truncate">{l.name}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Leave Echo a Note */}
      <div className="glass-panel rounded-2xl p-3.5 border border-white/10 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-fissal-amethyst" />
            <h3 className="font-semibold text-xs text-white">Leave Echo a Note</h3>
          </div>
          <Heart className="w-4 h-4 text-rose-400" />
        </div>

        <p className="text-xs text-slate-300">
          Have a question, grocery item, or need something checked? Drop a note directly onto Echo's server desk!
        </p>

        {noteStatus && (
          <div className="p-2.5 rounded-xl bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs flex items-center gap-2">
            <CheckCircle className="w-4 h-4 shrink-0" />
            <span>{noteStatus}</span>
          </div>
        )}

        <form onSubmit={handleSendNote} className="space-y-2">
          <div className="flex gap-2">
            <input
              type="text"
              value={noteSender}
              onChange={(e) => setNoteSender(e.target.value)}
              placeholder="Your Name (e.g. Mom)"
              className="w-1/3 bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-400"
            />
            <input
              type="text"
              value={noteMessage}
              onChange={(e) => setNoteMessage(e.target.value)}
              placeholder="Write your note here..."
              className="flex-1 bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-400"
            />
          </div>

          <button
            type="submit"
            disabled={!noteMessage.trim() || sendingNote}
            className="w-full py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-bold text-xs flex items-center justify-center gap-1.5 shadow-verdant-glow transition-all disabled:opacity-40"
          >
            <Send className="w-3.5 h-3.5" /> Leave Note for Echo
          </button>
        </form>
      </div>
    </div>
  );
};
